import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';

// 建立 axios 實例
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 請求攔截器 - 加入 Token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 回應攔截器 - 處理錯誤
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Token 過期，嘗試刷新
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        
        if (refreshToken) {
          const response = await axios.post('/api/auth/refresh', {
            refreshToken
          });

          if (response.data.success) {
            const { token } = response.data.data;
            
            // 更新 Token
            useAuthStore.getState().updateUser({ token });
            
            // 重試原始請求
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh 失敗，登出
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// 認證 API
// ============================================

export const authApi = {
  login: (credential, password) =>
    api.post('/auth/login', { credential, password }),
  
  logout: () =>
    api.post('/auth/logout'),
  
  me: () =>
    api.get('/auth/me'),
  
  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/change-password', { currentPassword, newPassword })
};

// ============================================
// 客戶 API
// ============================================

export const customerApi = {
  list: (params) =>
    api.get('/customers', { params }),
  
  get: (id) =>
    api.get(`/customers/${id}`),
  
  create: (data) =>
    api.post('/customers', data),
  
  update: (id, data) =>
    api.put(`/customers/${id}`, data),
  
  delete: (id) =>
    api.delete(`/customers/${id}`),
  
  addNote: (id, note) =>
    api.post(`/customers/${id}/notes`, note),
  
  checkCredit: (id, amount) =>
    api.get(`/customers/${id}/credit-check`, { params: { amount } })
};

// ============================================
// 商品 API
// ============================================

export const productApi = {
  list: (params) =>
    api.get('/products', { params }),
  
  get: (id) =>
    api.get(`/products/${id}`),
  
  create: (data) =>
    api.post('/products', data),
  
  update: (id, data) =>
    api.put(`/products/${id}`, data),
  
  checkStock: (id, quantity) =>
    api.get(`/products/${id}/stock-check`, { params: { quantity } }),
  
  getPrice: (id, tier, quantity) =>
    api.get(`/products/${id}/price`, { params: { tier, quantity } })
};

// ============================================
// 報價單 API
// ============================================

export const quotationApi = {
  list: (params) =>
    api.get('/quotations', { params }),
  
  get: (id) =>
    api.get(`/quotations/${id}`),
  
  create: (data) =>
    api.post('/quotations', data),
  
  update: (id, data) =>
    api.put(`/quotations/${id}`, data),
  
  delete: (id) =>
    api.delete(`/quotations/${id}`),
  
  toggleTax: (id, displayTaxIncluded) =>
    api.post(`/quotations/${id}/toggle-tax`, { displayTaxIncluded }),
  
  send: (id, method) =>
    api.post(`/quotations/${id}/send`, { method }),
  
  convertToOrder: (id) =>
    api.post(`/quotations/${id}/convert-to-order`),
  
  createVersion: (id, data) =>
    api.post(`/quotations/${id}/create-version`, data),
  
  getPDF: (id) =>
    api.get(`/quotations/${id}/pdf`, { responseType: 'blob' })
};

// ============================================
// OCR API
// ============================================

export const ocrApi = {
  processImage: (formData) =>
    api.post('/ocr/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  createQuotation: (formData) =>
    api.post('/ocr/create-quotation', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  verify: (recordId, corrections) =>
    api.post(`/ocr/verify/${recordId}`, { corrections }),
  
  history: (params) =>
    api.get('/ocr/history', { params }),
  
  stats: () =>
    api.get('/ocr/stats'),
  
  batch: (formData) =>
    api.post('/ocr/batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
};

// ============================================
// 訂單 API
// ============================================

export const orderApi = {
  list: (params) =>
    api.get('/orders', { params }),
  
  get: (id) =>
    api.get(`/orders/${id}`),
  
  updateStatus: (id, status, tracking) =>
    api.put(`/orders/${id}/status`, { status, tracking }),
  
  updatePayment: (id, amount, method) =>
    api.post(`/orders/${id}/payment`, { amount, method }),
  
  ship: (id, trackingNumber, shippingMethod) =>
    api.post(`/orders/${id}/ship`, { trackingNumber, shippingMethod }),
  
  cancel: (id, reason) =>
    api.post(`/orders/${id}/cancel`, { reason })
};

// ============================================
// 報表 API
// ============================================

export const reportApi = {
  quotationStats: (startDate, endDate) =>
    api.get('/reports/quotation-stats', { params: { startDate, endDate } }),
  
  salesPerformance: (startDate, endDate) =>
    api.get('/reports/sales-performance', { params: { startDate, endDate } }),
  
  customerRanking: (startDate, endDate, limit) =>
    api.get('/reports/customer-ranking', { params: { startDate, endDate, limit } })
};

export default api;
